import React, { useEffect } from "react";
import {
    analysis,
    arrowL,
    arrowR,
    boost,
    callOutgoing,
    challenge,
    dragDrop,
    group,
    groupOpen,
    homeContent1,
    homeContent2,
    homeContent3,
    homeContent4,
    homeMain,
    job,
    machineLearning,
    mbContent1,
    mbContent2,
    mbContent3_1,
    mbContent3_2,
    mbContent4,
    optimizeResources,
    phone,
    strengthenCollaboration,
} from "../../assets/images/image";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { APP_URL } from "../../configs/default.config";

const defaultReset = 7000;

const HomeMobile = () => {
    const [positionContent, setPositionContent] = React.useState(1);
    const [direction, setDirection] = React.useState<"left" | "right">("left");
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: false
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [controls, inView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3,
            }
        }
    };

    const fadeInVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    };

    const slideInFromLeft = {
        hidden: { x: -100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const slideInFromRight = {
        hidden: { x: 100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
    };

    const content = [
        {
            position: 1,
            url: [mbContent1],
            className: "w-full flex-shrink-0",
            text: "Auto-generate crystal-clear task briefs",
        },
        {
            position: 2,
            url: [mbContent2],
            className: "w-full flex-shrink-0",
            text: "AI-prioritized schedules at a glance",
        },
        {
            position: 3,
            url: [mbContent3_1, mbContent3_2],
            className: "w-full flex-shrink-0",
            text: "Smart load-balancing across skillsets",
        },
        {
            position: 4,
            url: [mbContent4],
            className: "w-full flex-shrink-0",
            text: "AI-prioritized schedules at a glance",
        },
    ];

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setPositionContent((prev) => (prev < 4 ? prev + 1 : 1));
        }, defaultReset);

        return () => clearTimeout(timeout);
    }, [positionContent]);

    const handleChangeContent = (type: "next" | "prev") => {
        setPositionContent((prev) => {
            if (type === "next") return prev < 4 ? prev + 1 : 1;
            return prev > 1 ? prev - 1 : 4;
        });
    };

    const workContent = [
        {
            position: 1,
            url: machineLearning,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[30px] mb-[10px] text-base font-bold leading-none w-full ms-1">Smart Task Briefs & Idea Labs</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Instant task descriptions with <strong>built-in context, deadlines and resource estimates</strong>.</li>
                        <li>Brainstorm mode kicks off project proposals in <strong>seconds</strong>.</li>
                    </ul>
            `
        },
        {
            position: 2,
            url: dragDrop,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[30px] mb-[10px] text-base font-bold leading-none w-full ms-1">Drag-and-Drop with Live AI</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Move cards and <strong>watch AI flag potential bottlenecks</strong> with color-coded alerts.</li>
                        <li>In-board tips: <em>"Consider assigning Maria—she's 20% under capacity"</em></li>
                    </ul>
            `
        },
        {
            position: 3,
            url: challenge,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[30px] mb-[10px] text-base font-bold leading-none w-full ms-1">AI-Backed Assignment Engine</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Profiles auto-updated with <strong>skills, past performance, availability</strong>.</li>
                        <li>One-click team suggestions with time-to-complete predictions.</li>
                    </ul>
            `
        },
        {
            position: 4,
            url: analysis,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[30px] mb-[10px] text-base font-bold leading-none w-full ms-1">Dynamic Analytics & Forecasting</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li><strong>Interactive charts</strong> that update as tasks move.</li>
                        <li>"What if" slider for <strong>workload spikes and deadline shifts</strong>.</li>
                    </ul>
            `
        },
        {
            position: 5,
            url: job,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
                    <div class="text-[#B1B1B1] mt-[30px] mb-[10px] text-base font-bold leading-none w-full ms-1">Industry-Tailored Templates</div >
                    <ul class="text-[#494949] text-base font-normal leading-6 space-y-1 list-disc list-outside pl-5">
                        <li>Select from Marketing, Software, Healthcare or Custom Matrix.</li>
                        <li>Pre-built integrations: GitHub, Jira, Salesforce, Asana.</li>
                    </ul>
            `
        },
    ];

    const challengeContent = [
        {
            position: 1,
            url: boost,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[30px] text-base font-semibold leading-none ms-1">Boost Productivity</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Automated briefs</strong> and <strong>smart assignments</strong> free your team to invent, not busywork.</li>
            </ul>
          `
        },
        {
            position: 2,
            url: strengthenCollaboration,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[30px] text-base font-semibold leading-none ms-1">Strengthen Collaboration</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Live updates</strong>, shared AI notes and in-app comments <strong>eliminate miscommunication</strong>.</li>
            </ul>
          `
        },
        {
            position: 3,
            url: group,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[30px] text-base font-semibold leading-none ms-1">Deliver Consistently</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li><strong>Predictive timelines</strong> and <strong>proactive alerts</strong> keep every milestone <strong>on track</strong>.</li>
            </ul>
          `
        },
        {
            position: 4,
            url: optimizeResources,
            className: "w-[48px] h-[48px] flex-shrink-0",
            content: `
            <div class="text-[#B1B1B1] mt-[30px] text-base font-semibold leading-none ms-1">Optimize Resources:</div>
            <ul class="text-[#494949] text-[16px] font-normal leading-[24px] mt-[12px] list-disc list-outside pl-5">
              <li>Cut costs with data-driven workload balancing and avoid last-minute scrambles.</li>
            </ul>
          `
        }
    ];

    return (
        <>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="flex flex-col md:flex-row items-center justify-between mt-[73px]"
            >
                <motion.div variants={itemVariants} className="w-full px-6 text-center">
                    <p className="text-[24px] font-bold leading-[normal] text-[#B1B1B1]">
                        Unleash Project Potential – Empower Your Team
                    </p>
                </motion.div>
                <motion.div variants={itemVariants} className="w-full px-6 mt-[16px]">
                    <h1 className="text-[#494949] text-[26px] leading-[40px] font-[274]">
                        Deliver projects{" "}
                        <span className="text-[#3A57FF] font-extrabold text-[26px] leading-[40px]">
                            30% Faster
                        </span>{" "}
                        with intelligent task automation
                    </h1>
                </motion.div>
                <motion.div variants={itemVariants} className="w-full px-6 mt-[16px]">
                    <p className="text-[#494949] text-[16px] leading-[24px] font-normal">
                        Automate assignments, track performance, and hit deadlines with intelligent
                        insights—no manual juggling required.
                    </p>
                </motion.div>
                <motion.img
                    className="w-full flex-shrink-0 mt-[16px]"
                    variants={slideInFromRight}
                    src={homeMain}
                    alt="Task automation mockup"
                />
                <motion.div variants={itemVariants} className="w-full px-6 mt-[16px]">
                    <button
                        onClick={() => window.location.href = APP_URL}
                        className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA] w-full">
                        <span className="flex items-center justify-center mb-1">Try It for Free &nbsp;</span>
                        <img
                            src={groupOpen}
                            alt="Task automation mockup"
                            className="h-[13.87px] w-[11.39px]"
                        />
                    </button>
                </motion.div>
                <motion.div variants={itemVariants} className="w-full px-6 mt-[16px]">
                    <button onClick={() => window.location.href = "/contact"} className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] border border-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#494949] w-full">
                        <span className="flex items-center justify-center mb-1">Contact Sales &nbsp;</span>
                        <img
                            src={callOutgoing}
                            alt="Task automation mockup"
                            className="h-[24px] w-[24px]"
                        />
                    </button>
                </motion.div>
                <motion.div variants={slideInFromRight} className="w-full px-6 mt-[32px] text-[#494949] text-[26px] leading-[40px] font-[274]">
                    UB Dashboard adapts to your unique needs, serving a wide array of sectors including
                </motion.div>
                <motion.div variants={slideInFromRight} className="w-full px-6 text-[#494949] text-[16px] leading-[24px] font-normal mt-[16px]">
                    Marketing, App Development, Light Industry, Oil & Gas, Industrial, Consumer
                    Goods, Healthcare, Consumer Services, Telecommunications, Utilities, Financial
                    Services, Technology, Materials, and more.
                </motion.div>
                <motion.div variants={slideInFromRight} className="w-full px-6 text-[#494949] text-[16px] leading-[24px] font-normal mt-[16px]">
                    Ultimate Boards significantly reduce human resource and energy costs, boosting
                    team productivity and accelerating project completion through advanced AI.
                </motion.div>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariants}
                    className="w-full px-6"
                >
                    {content.map((item, index) => (
                        <div key={index}>
                            <motion.div variants={itemVariants} className="w-full text-center mt-[70px] mb-[32px]">
                                <div className="w-full inline-flex h-[56px] px-4 py-1 justify-center items-center gap-4 flex-shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-center text-[#FAFAFA] text-[16px] leading-[24px] font-bold">
                                    {item.text}
                                </div>
                            </motion.div>
                            {item.url.map((url, i) => (
                                <motion.img
                                    key={`url-${i}`}
                                    className={`${item.className} ${i !== item.url.length - 1 ? "mb-[40px]" : ""}`}
                                    variants={slideInFromRight}
                                    src={url}
                                    alt="Task automation mockup"
                                />
                            ))}
                        </div>
                    ))}
                </motion.div>
                <motion.div variants={itemVariants} className="w-full px-6 mt-[32px] text-[#494949] text-[26px] leading-[40px] font-[274]">
                    Meet the AI That Works for You
                </motion.div>
                <motion.div
                    variants={containerVariants}
                    className="flex flex-col w-full px-6"
                >
                    {workContent.map((item) => (
                        <motion.div
                            key={item.position}
                            variants={itemVariants}
                            className="flex flex-row items-center gap-3 w-full h-auto"
                        >
                            <div className="flex items-center justify-center">
                                <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                            </div>
                            <div className="w-full ps-2" dangerouslySetInnerHTML={{ __html: item.content }} />
                        </motion.div>
                    ))}
                </motion.div>
                <motion.div variants={itemVariants} className="w-full px-6 mt-[32px] text-[#494949] text-[26px] leading-[40px] font-[274]">
                    From Challenges to Triumphs: The UB Dashboard Impact
                </motion.div>
                <motion.div
                    variants={containerVariants}
                    className="flex flex-col w-full px-6"
                >
                    {challengeContent.map((item) => (
                        <motion.div
                            key={item.position}
                            variants={itemVariants}
                            className="flex flex-row items-center gap-3 w-full h-auto"
                        >
                            <div className="flex items-center justify-center">
                                <img src={item.url} className={item.className} alt={`icon-${item.position}`} />
                            </div>
                            <div className="w-full ps-2" dangerouslySetInnerHTML={{ __html: item.content }} />
                        </motion.div>
                    ))}
                </motion.div>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                    className="w-full px-6"
                >
                    <motion.div variants={itemVariants} className="text-[#494949] text-[26px] leading-[40px] font-[274] mt-[64px]">
                        Measure Your Success: <span className="text-[#3A57FF] font-bold">UB Dashboard ROI</span>
                    </motion.div>
                    <motion.div variants={itemVariants} className="text-[#494949] text-[26px] leading-[40px] font-normal mt-[32px]">
                        Embed a mini-app where visitors can plug in
                    </motion.div>
                    <motion.div variants={itemVariants} className="text-[#494949] text-[16px] leading-[24px] font-normal mt-[16px]">
                        <span className="text-[#3A57FF] font-bold">Output: </span>projected time saved, cost reduction, and payoff period.
                    </motion.div>
                    <motion.img
                        className="w-full flex-shrink-0 mt-[32px]"
                        variants={slideInFromRight}
                        src={phone}
                        alt="Task automation mockup"
                    />
                    <motion.div className="flex flex-col w-full h-auto bg-[#3A57FF] mt-[32px] justify-center">
                        <div className="p-2 max-w-md mx-auto">
                            <ul className="text-[#FAFAFA] text-[16px] leading-[24px] font-bold">
                                <li className="flex items-center">
                                    <span >1. Team Size</span>
                                </li>
                                <li className="flex items-center">
                                    <span >2. Average tasks per week</span>
                                </li>
                                <li className="flex items-center">
                                    <span >3. Billable rate</span>
                                </li>
                            </ul>
                        </div>
                    </motion.div>
                    <motion.div className="flex justify-center p-4 space-x-4 mt-[32px]">
                        <h1 className="text-[#494949] text-center font-inter text-[64px] font-bold leading-none">
                            $20
                        </h1>
                        <div className="text-right mt-1">
                            <h2 className="ms-2 text-[#3A57FF] text-center text-[24px] font-bold leading-none">
                                Pro Plan
                            </h2>
                            <p className="text-[#494949] text-[32px] font-normal leading-none font-inter">
                                /Month
                            </p>
                        </div>
                    </motion.div>
                    <motion.div variants={itemVariants} className="w-full mt-[32px]">
                        <button
                            onClick={() => window.location.href = APP_URL}
                            className="inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA] w-full">
                            <span className="flex items-center justify-center mb-1">Get Started- It’s Free &nbsp;</span>
                            <img
                                src={groupOpen}
                                alt="Task automation mockup"
                                className="h-[13.87px] w-[11.39px]"
                            />
                        </button>
                    </motion.div>
                </motion.div>
            </motion.div>
        </>
    );
};

export default HomeMobile;